(() => {
  'use strict';
  if (window.YoLinkCatalogModel) return;
  const matches = (source, value) => !value || (source || '').split('|').includes(value);
  const matchesQuery = (record, query) => {
    // Format is catalog-owned and separate from the card's indexed text.
    // Keep aliases explicit so model/SKU queries retain their literal meaning.
    const format = record.format === 'Accessory' ? 'accessory accessories' : record.format;
    const source = [record.search || [record.title, record.model, record.handle, record.category, record.family].join(' '), format].join(' ').toLowerCase();
    return query.toLowerCase().trim().split(/\s+/).filter(Boolean).every(word => source.includes(word));
  };
  const featuredBrowse = state => state.sort === 'featured' && !state.query
    && state.category !== 'Hubs & connectivity' && state.hub !== 'Provides hub'
    && !state.formats.includes('Accessory');
  const matchesSelection = (record, state) => record.visibility !== 'Hidden'
    && (!featuredBrowse(state) || record.featuredExcluded !== 'true')
    && (!state.newReleases || record.newRelease === 'true')
    && matches(record.category, state.category) && matches(record.family, state.family)
    && (!state.formats.length || state.formats.includes(record.format))
    && (!state.hub || record.hub === state.hub)
    && (!state.available || record.available === 'true') && matchesQuery(record, state.query);
  // Approved storefront families use product-ID-gated Liquid data and reciprocal member lists.
  // Legacy same-model pack grouping remains available for staff-managed offers.
  const groupKey = record => {
    if (record.catalogGroup) {
      const members = (record.catalogMembers || '').split('|').filter(Boolean);
      if (members.length < 2 || new Set(members).size !== members.length || !members.includes(record.handle) || !record.catalogLabel) return '';
      return JSON.stringify(['catalog', record.catalogGroup, [...members].sort()]);
    }
    if (!['Single device', 'Multipack'].includes(record.format) || !record.model || record.model.includes('|') || !record.packLabel) return '';
    const offers = [...new Set((record.offers || '').split('|').filter(Boolean))].sort();
    if (offers.length < 2 || !offers.includes(record.handle)) return '';
    return JSON.stringify([record.model, offers, record.category, record.family, record.hub]);
  };
  const popular = (a, b) => Number(a.popularIndex) - Number(b.popularIndex);
  const presentations = (records, allRecords, group, featured = false) => {
    const groups = new Map();
    for (const record of records) {
      const key = group && groupKey(record) || record;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(record);
    }
    return [...groups.values()].map(matching => {
      const sorted = [...matching].sort(popular);
      const pinned = featured && sorted.filter(item => item.available === 'true' && Number(item.featuredRank) > 0)
        .sort((a, b) => Number(a.featuredRank) - Number(b.featuredRank))[0];
      const record = pinned || sorted.find(item => item.format === 'Single device' && item.available === 'true')
        || sorted.find(item => item.available === 'true') || sorted[0];
      const rank = Number(sorted[0].popularIndex);
      const key = group && groupKey(record);
      // Rank only matching offers, but disclose unavailable native pack choices.
      const offers = key ? allRecords.filter(item => item.visibility !== 'Hidden' && groupKey(item) === key
        && (!featured || item.featuredExcluded !== 'true')).sort(popular) : sorted;
      return { record, offers, rank, featuredRank: pinned ? Number(pinned.featuredRank) : 0 };
    });
  };
  const compare = (a, b, sort, rankA, rankB) => {
    if (sort === 'featured' && Number(a.featuredRank) !== Number(b.featuredRank)) {
      if (Number(a.featuredRank) > 0 && Number(b.featuredRank) > 0) return Number(a.featuredRank) - Number(b.featuredRank);
      if (Number(a.featuredRank) > 0) return -1;
      if (Number(b.featuredRank) > 0) return 1;
    }
    if (sort === 'newest') return (Number(b.createdAt) || 0) - (Number(a.createdAt) || 0)
      || Number(a.popularIndex) - Number(b.popularIndex);
    if (sort === 'price-ascending') return Number(a.price) - Number(b.price);
    if (sort === 'price-descending') return Number(b.price) - Number(a.price);
    if (sort === 'title') return a.title.localeCompare(b.title);
    return (rankA ?? Number(a.popularIndex)) - (rankB ?? Number(b.popularIndex));
  };
  // Translate only Chinese discovery terms; keep the shopper's query and URL intact.
  // Weglot owns the translations. These two English normalizations bridge its
  // output to our catalog's "&" and Hub terminology without changing English search.
  const createQueryResolver = ({ language, translate, onChange,
    schedule = setTimeout, cancel = clearTimeout }) => {
    const cache = new Map();
    let current, debounce;
    return query => {
      const lang = language();
      const key = `${lang || 'en'}:${query}`;
      if (current?.key === key) return current;
      cancel(debounce);
      const needsTranslation = lang === 'zh' && /[\u3400-\u9fff]/.test(query);
      current = { key, query: cache.get(key) || query,
        pending: needsTranslation && !cache.has(key), error: false };
      if (!current.pending) return current;
      const requested = current;
      debounce = schedule(() => {
        const finish = (text, failed = false) => {
          if (requested.finished) return;
          requested.finished = true;
          cancel(requested.timeout);
          const terms = typeof text === 'string' ? text.replace(/\bgateways?\b/gi, 'hub')
            .replace(/\band\b/gi, ' ').replace(/\s+/g, ' ').trim() : '';
          if (!failed && terms) {
            cache.set(key, terms);
            if (cache.size > 50) cache.delete(cache.keys().next().value);
          }
          if (current !== requested) return;
          current.query = cache.get(key) || query;
          current.pending = false;
          current.error = failed || !terms;
          onChange();
        };
        requested.timeout = schedule(() => finish('', true), 8000);
        try { translate(query, text => finish(text)); }
        catch { finish('', true); }
      }, 250);
      return current;
    };
  };
  window.YoLinkCatalogModel = { matches, matchesQuery, featuredBrowse, matchesSelection, groupKey, presentations, compare, createQueryResolver };
})();
